import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SimplePeer from 'simple-peer';
import { useAuth } from '../../contexts/AuthContext';
import { interviewsService } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import VideoDisplay from './components/VideoDisplay';
import MeetingControls from './components/MeetingControls';
import ChatPanel from './components/ChatPanel';
import ConnectionStatus from './components/ConnectionStatus';
import RecordingControls from './components/RecordingControls';

const VideoInterviewRoom = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const interviewId = searchParams?.get('interviewId');
  const matchId = searchParams?.get('matchId');

  // WebRTC state
  const [peer, setPeer] = useState(null);
  const [stream, setStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('good');

  // Meeting controls state
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  // Interview data
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const screenStreamRef = useRef(null);

  // Load interview details
  useEffect(() => {
    const loadInterview = async () => {
      if (!interviewId) {
        setError('No interview ID provided');
        setLoading(false);
        return;
      }

      try {
        const data = await interviewsService?.getById(interviewId);
        setInterview(data);
      } catch (err) {
        setError(err?.message || 'Failed to load interview');
      } finally {
        setLoading(false);
      }
    };

    loadInterview();
  }, [interviewId]);

  // Initialize media stream
  useEffect(() => {
    const initializeMedia = async () => {
      try {
        const mediaStream = await navigator.mediaDevices?.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        setStream(mediaStream);
        if (localVideoRef?.current) {
          localVideoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error('Error accessing media devices:', err);
        setError('Unable to access camera/microphone. Please check permissions.');
      }
    };

    initializeMedia();

    return () => {
      if (stream) {
        stream?.getTracks()?.forEach(track => track?.stop());
      }
      if (screenStreamRef?.current) {
        screenStreamRef?.current?.getTracks()?.forEach(track => track?.stop());
      }
    };
  }, []);

  // Initialize WebRTC peer connection
  useEffect(() => {
    if (!stream || !user) return;

    const initiator = user?.id === interview?.candidateId;

    const newPeer = new SimplePeer({
      initiator,
      stream,
      trickle: false,
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      }
    });

    newPeer?.on('signal', (data) => {
      console.log('Signal data:', data);
      // In production, send this signal data to the other peer via Supabase realtime
    });

    newPeer?.on('stream', (remoteStream) => {
      setRemoteStream(remoteStream);
      if (remoteVideoRef?.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
      setIsConnected(true);
    });

    newPeer?.on('connect', () => {
      setIsConnected(true);
    });

    newPeer?.on('error', (err) => {
      console.error('Peer connection error:', err);
      setError('Connection error. Please try refreshing.');
    });

    setPeer(newPeer);

    return () => {
      newPeer?.destroy();
    };
  }, [stream, user, interview]);

  // Toggle mute
  const handleToggleMute = () => {
    if (stream) {
      stream?.getAudioTracks()?.forEach(track => {
        track.enabled = !track?.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle video
  const handleToggleVideo = () => {
    if (stream) {
      stream?.getVideoTracks()?.forEach(track => {
        track.enabled = !track?.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  // Toggle screen sharing
  const handleToggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStreamRef?.current) {
        screenStreamRef?.current?.getTracks()?.forEach(track => track?.stop());
        screenStreamRef.current = null;
      }

      // Restore camera stream
      if (stream && peer) {
        const videoTrack = stream?.getVideoTracks()?.[0];
        const sender = peer?._pc?.getSenders()?.find(s => s?.track?.kind === 'video');
        if (sender) {
          sender?.replaceTrack(videoTrack);
        }
      }

      setIsScreenSharing(false);
    } else {
      // Start screen sharing
      try {
        const screenStream = await navigator.mediaDevices?.getDisplayMedia({
          video: {
            cursor: 'always'
          },
          audio: false
        });

        screenStreamRef.current = screenStream;

        // Replace video track with screen track
        if (peer) {
          const screenTrack = screenStream?.getVideoTracks()?.[0];
          const sender = peer?._pc?.getSenders()?.find(s => s?.track?.kind === 'video');
          if (sender) {
            sender?.replaceTrack(screenTrack);
          }

          // Handle screen share stop
          screenTrack.onended = () => {
            handleToggleScreenShare();
          };
        }

        setIsScreenSharing(true);
      } catch (err) {
        console.error('Error sharing screen:', err);
      }
    }
  };

  // Toggle recording
  const handleToggleRecording = () => {
    if (isRecording) {
      // Stop recording logic
      setIsRecording(false);
    } else {
      // Start recording logic
      setIsRecording(true);
    }
  };

  // End call
  const handleEndCall = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
    }
    if (screenStreamRef?.current) {
      screenStreamRef?.current?.getTracks()?.forEach(track => track?.stop());
    }
    if (peer) {
      peer?.destroy();
    }
    navigate('/match-management');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#6B7280]">Loading interview room...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="#EF4444" />
          </div>
          <h2 className="text-xl font-semibold text-[#111827] mb-2">Unable to Join</h2>
          <p className="text-[#6B7280] mb-6">{error}</p>
          <button
            onClick={() => navigate('/match-management')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Return to Matches
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Video Interview - BlinD</title>
      </Helmet>

      <div className="min-h-screen bg-[#111827] flex flex-col">
        {/* Header */}
        <div className="bg-[#1F2937] border-b border-[#374151] px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon name="Video" size={18} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-white font-semibold text-sm md:text-base">
                  {interview?.matches?.opportunities?.roleTitle || 'Video Interview'}
                </h1>
                <p className="text-gray-400 text-xs">
                  {interview?.matches?.opportunities?.industry}
                </p>
              </div>
            </div>

            <ConnectionStatus
              isConnected={isConnected}
              quality={connectionQuality}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video Area */}
          <div className="flex-1 relative p-4">
            <VideoDisplay
              localVideoRef={localVideoRef}
              remoteVideoRef={remoteVideoRef}
              isVideoOff={isVideoOff}
              isScreenSharing={isScreenSharing}
              remoteStream={remoteStream}
            />
          </div>

          {/* Chat Panel */}
          {showChat && (
            <ChatPanel
              interviewId={interviewId}
              onClose={() => setShowChat(false)}
            />
          )}
        </div>

        {/* Controls Footer */}
        <div className="bg-[#1F2937] border-t border-[#374151] px-4 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-4">
              <RecordingControls
                isRecording={isRecording}
                onToggleRecording={handleToggleRecording}
              />

              <MeetingControls
                isMuted={isMuted}
                isVideoOff={isVideoOff}
                isScreenSharing={isScreenSharing}
                showChat={showChat}
                onToggleMute={handleToggleMute}
                onToggleVideo={handleToggleVideo}
                onToggleScreenShare={handleToggleScreenShare}
                onToggleChat={() => setShowChat(!showChat)}
                onEndCall={handleEndCall}
              />

              <div className="w-32" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoInterviewRoom;